import React, { useState } from 'react';
import {
  Box, Typography, TextField, Button, Card, CardContent,
  InputAdornment, Alert, Chip, useTheme, Stack, CircularProgress,
  Snackbar, alpha, Divider, Dialog, DialogTitle, DialogContent,
  DialogActions, Tooltip,
} from '@mui/material';
import { Search, Plus, KeyRound, ArrowRight, BookOpen } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import NerdSpinner from '@/components/NerdSpinner';
import sentinelNetwork from '@/lib/api/sentinelNetwork';
import { useDataContext } from '@/lib/DataContext';
import { ROLE } from '@/lib/constants';
import { searchOnboardingsByLeanIx, type OnboardingListItem } from './api';
import { isValidAppId, normalizeAppId } from './validation';
import OnboardingWizard from './OnboardingWizard';
import UserGuideDialog from './UserGuideDialog';


const OnboardingCreatePage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const initialLeanIxId = searchParams.get('leanIxId') || undefined;
  const navigate = useNavigate();
  const theme = useTheme();
  const { t } = useTranslation();
  const { data: entitlements } = useDataContext() || {};
  const canCreateProjects = [ROLE.SENTINEL_PROJECT_CREATION, ROLE.SENTINEL_POWER_USER].some(
    (r) => entitlements?.roles?.includes(r),
  );

  // If leanIxId was passed via URL, skip search and go straight to wizard
  const [phase, setPhase] = useState<'search' | 'wizard'>(initialLeanIxId ? 'wizard' : 'search');

  // Search state
  const [appId, setAppId] = useState('APP-');
  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState(false);
  const [results, setResults] = useState<OnboardingListItem[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Guide dialog state
  const [guideOpen, setGuideOpen] = useState(false);

  // Join state
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({ open: false, message: '', severity: 'success' });

  // Access request dialog state
  const [accessDialog, setAccessDialog] = useState<{ open: boolean; appId: string; projectName: string }>({ open: false, appId: '', projectName: '' });
  const [submittingAccess, setSubmittingAccess] = useState(false);

  // Wizard leanIxId (set when user clicks "Register New")
  const [wizardLeanIxId, setWizardLeanIxId] = useState<string | undefined>(initialLeanIxId);

  const handleSearch = async () => {
    if (!isValidAppId(appId)) {
      setError(t('search.appIdRequired'));
      return;
    }
    setSearching(true);
    setError(null);
    try {
      const data = await searchOnboardingsByLeanIx(appId.trim());
      setResults(data || []);
      setSearched(true);
    } catch {
      setError(t('search.searchFailed'));
      setResults([]);
      setSearched(true);
    } finally {
      setSearching(false);
    }
  };

  const openAccessDialog = (projectAppId: string, projectName: string) => {
    setAccessDialog({ open: true, appId: projectAppId, projectName });
  };

  const submitAccessRequest = async () => {
    setSubmittingAccess(true);
    try {
      await sentinelNetwork.post(`/config/project/${accessDialog.appId}/access-request`);
      setSnackbar({ open: true, message: 'Access request submitted — a project admin will review it shortly.', severity: 'success' });
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Failed to submit request';
      const severity = err?.response?.status === 409 ? 'info' : 'error';
      setSnackbar({ open: true, message: msg, severity });
    } finally {
      setSubmittingAccess(false);
      setAccessDialog({ open: false, appId: '', projectName: '' });
    }
  };

  const goToWizard = () => {
    const leanIxId = appId.trim() && isValidAppId(appId) ? appId.trim() : undefined;
    setWizardLeanIxId(leanIxId);
    setPhase('wizard');
  };

  // ── Wizard phase ──
  if (phase === 'wizard') {
    return (
      <Box sx={{ flexGrow: 1, minWidth: 0, height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column', p: { xs: 1.5, sm: 2 } }}>
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
          <OnboardingWizard mode="create" initialLeanIxId={wizardLeanIxId} />
        </Box>
        <UserGuideDialog open={guideOpen} onClose={() => setGuideOpen(false)} />
      </Box>
    );
  }

  // ── Search phase ──
  return (
    <Box
      sx={{
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        overflow: 'hidden',
        px: 3,
        py: { xs: 3, sm: 5 },
      }}
    >
      {/* Header */}
      <Box sx={{ width: '100%', maxWidth: 640, flexShrink: 0, textAlign: 'center' }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<BookOpen size={15} />}
            onClick={() => setGuideOpen(true)}
            sx={{ textTransform: 'none', fontSize: 13 }}
          >
            User Guide
          </Button>
        </Box>
        <Typography variant="h5" fontWeight={700}>Onboard a Project</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 3 }}>
          Enter your APP ID to check if the project already exists, or register a new one.
        </Typography>

        {/* Search input */}
        <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center' }}>
          <TextField
            label={t('search.appIdLabel')}
            placeholder={t('search.appIdPlaceholder')}
            size="small"
            required
            value={appId}
            disabled={searching}
            onChange={(e) => setAppId(normalizeAppId(e.target.value))}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
            error={!!appId.trim() && appId.trim() !== 'APP-' && !isValidAppId(appId)}
            helperText={appId.trim() !== 'APP-' && !isValidAppId(appId) ? t('onboarding.form.leanIxIdInvalid') : ''}
            sx={{ maxWidth: 320, flex: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={16} color={theme.palette.text.secondary} />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            onClick={handleSearch}
            disabled={!isValidAppId(appId) || searching}
            startIcon={searching ? <NerdSpinner size={16} /> : undefined}
          >
            {searching ? t('search.searching') : t('search.searchButton')}
          </Button>
        </Box>
      </Box>

      {/* Results area */}
      <Box sx={{ width: '100%', maxWidth: 640, flex: 1, minHeight: 0, overflow: 'auto', mt: 3 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* No results */}
        {searched && !error && results.length === 0 && (
          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
              No existing projects found for <strong>{appId.trim()}</strong>. You can register it as a new project.
            </Alert>
            <Button
              variant="contained"
              size="large"
              startIcon={<Plus size={18} />}
              onClick={goToWizard}
            >
              Register New Project
            </Button>
          </Box>
        )}

        {/* Results found */}
        {searched && results.length > 0 && (
          <>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
              {t('search.resultsFound', { count: results.length })}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3 }}>
              {results.map((item) => (
                <Card key={item.appId} variant="outlined" sx={{ borderRadius: 2 }}>
                  <CardContent sx={{ pb: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                      <Typography variant="subtitle1" fontWeight={600}>{item.config_name || item.appName}</Typography>
                      {item.project_phase && (
                        <Chip label={item.project_phase} size="small" variant="outlined" />
                      )}
                    </Box>
                    <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'flex-end' }}>
                      {item.is_member ? (
                        <Button
                          size="small"
                          variant="contained"
                          color="success"
                          endIcon={<ArrowRight size={14} />}
                          onClick={() => navigate(`/projects/${item.appId}`)}
                        >
                          Go to Project
                        </Button>
                      ) : (
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<KeyRound size={14} />}
                          onClick={() => openAccessDialog(item.appId, item.config_name || item.appName || item.appId)}
                        >
                          Request Access
                        </Button>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Don't see your project? Register it as a new one.
              </Typography>
              <Tooltip
                title={
                  results.length > 0 && !canCreateProjects
                    ? 'A project with this APP ID already exists — only Power Users and Project Creation roles can register another'
                    : ''
                }
                arrow
              >
                <span>
                  <Button
                    variant="contained"
                    startIcon={<Plus size={18} />}
                    onClick={goToWizard}
                    disabled={results.length > 0 && !canCreateProjects}
                  >
                    Register New Project
                  </Button>
                </span>
              </Tooltip>
            </Box>
          </>
        )}
      </Box>

      {/* Access type selection dialog */}
      <Dialog
        open={accessDialog.open}
        onClose={() => !submittingAccess && setAccessDialog({ open: false, appId: '', projectName: '' })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Request Access</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            You are requesting access to <strong>{accessDialog.projectName}</strong>.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            This will give you full access to project data, scan results, security findings, and project settings. A project admin will review your request.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setAccessDialog({ open: false, appId: '', projectName: '' })}
            disabled={submittingAccess}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={submitAccessRequest}
            disabled={submittingAccess}
            startIcon={submittingAccess ? <CircularProgress size={16} /> : undefined}
          >
            {submittingAccess ? 'Submitting…' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <UserGuideDialog open={guideOpen} onClose={() => setGuideOpen(false)} />
    </Box>
  );
};

export default OnboardingCreatePage;
